import type { SubscriptionPlanId, SubscriptionProductType, VodAccessType } from '@/types/subscription';

export interface SubscriptionPlanConfig {
  id: SubscriptionPlanId;
  name: string;
  productType: SubscriptionProductType;

  // Pricing (USD)
  basePrice: number;
  billingCycle: 'monthly';

  // Live access
  liveSessionsPerMonth: number;

  // VOD access
  vodAccessType: VodAccessType;
  vodTokensPerMonth: number;
  vodTrialDays?: number;

  // Bundle options (live plans only)
  canAddVodBundle: boolean;
  vodBundlePrice: number | null;
}

/**
 * Centralized subscription plan configuration (v1).
 *
 * Pricing notes:
 *   - VOD Unlimited standalone: $300/month (full library, no live sessions).
 *   - Live plans (starter/progress) can add the VOD Unlimited bundle at $40/month
 *     via the `canAddVodBundle` / `vodBundlePrice` fields.
 */
export const subscriptionPlans: Record<SubscriptionPlanId, SubscriptionPlanConfig> = {
  starter: {
    id: 'starter',
    name: 'Starter Studio Pass',
    productType: 'live',
    basePrice: 100,
    billingCycle: 'monthly',
    liveSessionsPerMonth: 4,
    vodAccessType: 'none',
    vodTokensPerMonth: 0,
    canAddVodBundle: true,
    vodBundlePrice: 40,
  },
  progress: {
    id: 'progress',
    name: 'Progress Studio Pass',
    productType: 'live',
    basePrice: 160,
    billingCycle: 'monthly',
    liveSessionsPerMonth: 8,
    vodAccessType: 'none',
    vodTokensPerMonth: 0,
    canAddVodBundle: true,
    vodBundlePrice: 40,
  },
  weekly_vod: {
    id: 'weekly_vod',
    name: '3 Recordings',
    productType: 'vod',
    basePrice: 60,
    billingCycle: 'monthly',
    liveSessionsPerMonth: 0,
    vodAccessType: 'token',
    // Default monthly token allocation. Per-user overrides are supported
    // via subscription.vodTokensMonthlyOverride (CRM admin tool).
    // Legacy subscribers created when this was 4 keep 4 because the
    // renewal CF preserves `vodTokensPerMonth` on the sub doc.
    vodTokensPerMonth: 3,
    canAddVodBundle: false,
    vodBundlePrice: null,
  },
  vod_unlimited: {
    id: 'vod_unlimited',
    name: 'VOD Unlimited',
    productType: 'vod',
    basePrice: 300,
    billingCycle: 'monthly',
    liveSessionsPerMonth: 0,
    vodAccessType: 'unlimited',
    vodTokensPerMonth: 0,
    canAddVodBundle: false,
    vodBundlePrice: null,
  },
  trial_vod: {
    id: 'trial_vod',
    name: 'Trial VOD',
    productType: 'trial',
    basePrice: 30,
    billingCycle: 'monthly', // treated as 1-time by billing integration; kept for shape consistency
    liveSessionsPerMonth: 0,
    vodAccessType: 'trial',
    vodTokensPerMonth: 0,
    vodTrialDays: 3,
    canAddVodBundle: false,
    vodBundlePrice: null,
  },
};

export function getSubscriptionPlan(planId: string): SubscriptionPlanConfig | null {
  const key = planId as SubscriptionPlanId;
  return subscriptionPlans[key] ?? null;
}

